/**
 * "Raising an escalation" walkthrough.
 *
 * Starts on the Escalations module page. Walks the user through opening the
 * Raise Escalation modal and explains every field — but ends before submission
 * so the user is not forced to actually raise an escalation as part of the tour.
 *
 * Targets used:
 *   data-tour="raise-escalation-btn"      — Raise Escalation button (both staff/mgmt views)
 *   data-tour="raise-escalation-form"     — wrapper around the form fields
 *   data-tour="raise-escalation-submit"   — "Submit Escalation" button
 *   data-tour="raise-escalation-cancel"   — "Cancel" button
 *
 * Escalation Type list, field labels and validation rules mirror
 * RaiseEscalationModal.vue — keep in sync if those change.
 */

import { NEXT, BACK, START, SKIP, DONE } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

export const raiseEscalationTour = {
    id: 'raise-escalation',
    title: 'Raising an escalation',
    description: 'How to raise a compliance escalation against a client — when to do it, what each field means, and what happens after submission.',
    autoLaunchOn: { page: 'escalations' },

    steps: [
        {
            id: 'intro',
            title: 'Raising an escalation',
            text: `An escalation flags a compliance concern — unusual transactions, identity
                discrepancies, sanctions matches, change in client circumstances, or anything
                else that needs Compliance Officer attention.
                <br><br>
                <strong>Tipping-off warning:</strong> never discuss an escalation with the
                client it relates to, or with colleagues not directly involved. Doing so may
                breach s 123 of the AML/CTF Act 2006.`,
            buttons: [START],
        },
        {
            id: 'raise-btn',
            attachTo: { element: '[data-tour="raise-escalation-btn"]', on: 'left' },
            beforeShowPromise: () => waitForElement('[data-tour="raise-escalation-btn"]'),
            title: 'Click "Raise Escalation"',
            text: `The <strong>Raise Escalation</strong> button opens the form. You can
                also raise one from a specific client's detail page, or via the warning-icon
                button in the client list — those pre-fill the client field for you.`,
            advanceOn: { selector: '[data-tour="raise-escalation-btn"]', event: 'click' },
        },
        {
            id: 'form-fields',
            attachTo: { element: '[data-tour="raise-escalation-form"]', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="raise-escalation-submit"]'),
            // Cancel if the modal closes (X or Cancel) — the submit
            // button only exists while the modal is open.
            cancelIfMissing: '[data-tour="raise-escalation-submit"]',
            title: 'The escalation form',
            text: `Fill in:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Client</strong> (required) — which client this concerns.</li>
                    <li><strong>Escalation Type</strong> (required) — pick from a fixed list:
                        unusual transaction or behaviour; identity verification concerns;
                        new risk factors not in risk assessment; change in client
                        circumstances; adverse media or information; complex beneficial
                        ownership; positive sanctions match; high-risk client identified.</li>
                    <li><strong>Date of Observation</strong> (required) — when you first
                        noticed the concern. Cannot be a future date.</li>
                    <li><strong>Description of Concern</strong> (required, min 20 chars) —
                        the substance of what you observed. Be specific and factual.</li>
                    <li><strong>Supporting Information</strong> (optional) — extra context.</li>
                    <li><strong>Attachment</strong> (optional) — drag or click to upload
                        evidence (jpg, png, or pdf, up to 5MB).</li>
                </ul>`,
            // No Previous button here — the prior tour step lives outside
            // the modal that the user has just opened. Going back to it
            // while the modal is still on screen reads as broken.
            buttons: [NEXT],
        },
        {
            id: 'submit',
            attachTo: { element: '[data-tour="raise-escalation-submit"]', on: 'top' },
            cancelIfMissing: '[data-tour="raise-escalation-submit"]',
            title: 'Submit the escalation',
            text: `Click <strong>Submit Escalation</strong> to lodge the concern. The
                escalation is created with status <strong>Open</strong>, and the firm's
                Compliance Officers are notified.
                <br><br>
                From there, the CO triages the escalation, may move it to
                <strong>Under Review</strong> while investigating, and either resolves it
                directly or routes it to a Senior Manager for approval — see the
                <em>Handling an escalation</em> tour for that side of the process.`,
            buttons: [BACK, DONE],
        },
    ],
};
